// src/App.js
// Main App Component - handles authentication and routing

import React, { useState, useEffect } from 'react';
import { subscribeToAuthState, consumeInboundSSOToken, getCurrentUserProfile } from './firebase/auth';
import { runPendingTutorAction } from './firebase/tutorLinks';
import { auth } from './firebase/config';
import { signInWithCustomToken, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import Login from './components/Login';
import StudentDashboard from './components/StudentDashboard';
import StudentPendingApproval from './components/StudentPendingApproval';
import MarkerDashboard from './components/MarkerDashboard';
import HeadMarkerDashboard from './HeadMarkerDashboard';
import PlatformAdminView from './components/PlatformAdminView';
import StudentRegistration from './components/StudentRegistration';
import ExamWindowApp from './components/ExamWindowApp';
import LegalPage from './components/LegalPage';
import { BookOpen, AlertTriangle, LifeBuoy } from 'lucide-react';
import { rememberTutor } from './utils/rememberedTutor';
import { DEFAULT_ORG_ID } from './firebase/orgs';

// Set by PlatformAdminView's "Enter dashboard" button before it signs Stuart
// in as the tutor's own account - read here so a persistent banner (and a
// way back) survives that auth swap. Session-scoped deliberately: if the
// tab closes mid-session, Stuart just signs back in normally next time.
const SUPPORT_SESSION_KEY = 'homeworkSolutions.supportSession';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showPlatformAdmin, setShowPlatformAdmin] = useState(false);
  const [supportSession, setSupportSession] = useState(null);
  const [exitingSupportSession, setExitingSupportSession] = useState(false);
  // Set when an inbound cross-app SSO redirect (see consumeInboundSSOToken)
  // carried a viewSubmission id - StudentDashboard consumes this once to
  // jump straight to that submission's marked PDF, then it's cleared via
  // onAutoOpenSubmissionHandled so it doesn't re-trigger on a later re-render.
  const [pendingMarkedSubmissionId, setPendingMarkedSubmissionId] = useState(null);

  // Re-read on every auth state change, not just on mount - entering (or
  // exiting) a support session swaps `user` via signInWithCustomToken
  // without a full page reload, so a mount-only read would miss the write
  // PlatformAdminView/handleExitSupportSession just made to sessionStorage.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SUPPORT_SESSION_KEY);
      setSupportSession(raw ? JSON.parse(raw) : null);
    } catch {
      setSupportSession(null);
    }
  }, [user]);

  useEffect(() => {
    // Check URL for registration page
    if (window.location.pathname === '/register') {
      setShowRegistration(true);
      setLoading(false);
      return;
    }

    // Subscribe to authentication state changes — but first, consume any
    // inbound cross-app SSO token (e.g. redirected here from
    // ae-course-hosting) so the resulting sign-in is already in place
    // before the listener attaches.
    let unsubscribe;
    consumeInboundSSOToken().then((viewSubmissionId) => {
      if (viewSubmissionId) setPendingMarkedSubmissionId(viewSubmissionId);
    }).finally(() => {
      unsubscribe = subscribeToAuthState(async (profile) => {
        // Linked tutors: finish a "log in to add this tutor" from the
        // registration page, or land on the tutor an ae-course-hosting
        // sign-in named - then reload the profile it changed.
        if (profile && await runPendingTutorAction(profile)) {
          profile = (await getCurrentUserProfile()) || profile;
        }
        setUser(profile);
        setLoading(false);
      });
    });

    // Cleanup subscription on unmount
    return () => unsubscribe?.();
  }, []);

  // Lets the login page on this device show this tutor's logo next time.
  // Follows the active tutor, so a student who switches or moves tutor is
  // remembered with the new one from their next visit.
  useEffect(() => {
    if (user) rememberTutor(user.orgId || DEFAULT_ORG_ID, user.role);
  }, [user]);

  const handleLogout = () => {
    setUser(null);
  };

  const handleRegistrationSuccess = (userData) => {
    // After successful registration, redirect to login
    alert(`Account created successfully!\n\nWelcome ${userData.name}! You can now log in.`);
    setShowRegistration(false);
    window.history.pushState({}, '', '/');
  };

  const handleExitSupportSession = async () => {
    if (!supportSession) return;
    setExitingSupportSession(true);
    try {
      const functions = getFunctions(getApp());
      const mintReturnToAdminToken = httpsCallable(functions, 'mintReturnToAdminToken');
      const result = await mintReturnToAdminToken({ logId: supportSession.logId });
      sessionStorage.removeItem(SUPPORT_SESSION_KEY);
      setSupportSession(null);
      // Restore normal LOCAL persistence for Stuart's own admin account -
      // entering the support session switched persistence to SESSION (see
      // PlatformAdminView.handleEnterDashboard), so without this he'd be
      // logged out of his own account the next time he closes the tab too.
      await setPersistence(auth, browserLocalPersistence);
      await signInWithCustomToken(auth, result.data.token);
    } catch (error) {
      console.error('Exit support session error:', error);
      alert('Could not return to your admin account automatically. Please log out and sign back in with your own email.');
      setExitingSupportSession(false);
    }
  };

  const handleVerificationPending = () => {
    // User registered with email verification required — they're already signed out.
    // Just hide the registration screen; since user is null, the login page will show.
    setShowRegistration(false);
    window.history.pushState({}, '', '/');
  };

  // Public Terms and Privacy Policy pages - readable signed in or not
  const legalDoc = { '/terms': 'terms', '/privacy': 'privacy' }[window.location.pathname];
  if (legalDoc) {
    return <LegalPage doc={legalDoc} />;
  }

  // Show loading screen while checking auth
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f7fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <BookOpen size={48} />
          </div>
          <div style={{ fontSize: '18px', color: '#718096' }}>Loading...</div>
        </div>
      </div>
    );
  }

  // Show registration page
  if (showRegistration) {
    const params = new URLSearchParams(window.location.search);
    const cohortParam = params.get('cohort') || '';
    // Cohort codes are only unique within one tutor's own cohorts (see
    // cohorts.js), so a registration link now carries which tutor's cohort
    // this is - falls back to '' for a link shared before this existed,
    // which StudentRegistration/getCohortByCode treat as "look it up
    // unscoped, same as before."
    const orgParam = params.get('org') || '';
    return <StudentRegistration cohortCodeFromUrl={cohortParam} orgIdFromUrl={orgParam} onRegistrationSuccess={handleRegistrationSuccess} onVerificationPending={handleVerificationPending} />;
  }

  // Show login page if not authenticated
  if (!user) {
    return <Login onLoginSuccess={setUser} onRegisterClick={() => setShowRegistration(true)} />;
  }

  // Exam simulation window (opened via window.open from StudentDashboard)
  if (window.location.pathname === '/exam') {
    return <ExamWindowApp user={user} />;
  }

  // Platform admin: independent of role, so this works whichever role the
  // account underneath also has. Only ever reachable via the floating
  // button below, which only renders for an account with the flag set.
  if (user.platformAdmin && showPlatformAdmin) {
    return <PlatformAdminView user={user} onExit={() => setShowPlatformAdmin(false)} onLogout={handleLogout} />;
  }

  // Route based on user role
  let dashboard;
  if (user.role === 'student' && user.approvalPending === true) {
    // Their active tutor hasn't confirmed them yet (tutor approval)
    dashboard = <StudentPendingApproval user={user} onLogout={handleLogout} />;
  } else if (user.role === 'student') {
    dashboard = (
      <StudentDashboard
        user={user}
        onLogout={handleLogout}
        autoOpenSubmissionId={pendingMarkedSubmissionId}
        onAutoOpenSubmissionHandled={() => setPendingMarkedSubmissionId(null)}
      />
    );
  } else if (user.role === 'marker') {
    dashboard = <MarkerDashboard user={user} onLogout={handleLogout} />;
  } else if (user.role === 'head_marker') {
    dashboard = <HeadMarkerDashboard user={user} onLogout={handleLogout} />;
  } else {
    // Unknown role
    dashboard = (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f7fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
    <AlertTriangle size={48} />
  </div>
          <div style={{ fontSize: '18px', color: '#718096' }}>
            Unknown user role: {user.role}
          </div>
          <button
            onClick={handleLogout}
            style={{
              marginTop: '24px',
              padding: '12px 24px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Stuart, currently signed in AS the tutor via a support session - shown
  // instead of the normal platform-admin button below, since he isn't
  // signed in as himself right now. The dashboard underneath is untouched.
  const inSupportSession = supportSession && !user.platformAdmin;

  return (
    <>
      {dashboard}
      {inSupportSession && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          padding: '10px 20px',
          background: '#c05621',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          fontSize: '14px',
          fontWeight: '600',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
        }}>
          <LifeBuoy size={16} />
          Viewing {supportSession.orgName}'s dashboard as support access
          <button
            onClick={handleExitSupportSession}
            disabled={exitingSupportSession}
            style={{
              padding: '6px 14px',
              background: 'white',
              color: '#c05621',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '700',
              cursor: exitingSupportSession ? 'not-allowed' : 'pointer'
            }}
          >
            {exitingSupportSession ? 'Exiting...' : 'Exit support session'}
          </button>
        </div>
      )}
      {user.platformAdmin && (
        <button
          onClick={() => setShowPlatformAdmin(true)}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            padding: '10px 18px',
            background: '#2d3748',
            color: 'white',
            border: 'none',
            borderRadius: '999px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            zIndex: 1000
          }}
        >
          Platform Admin
        </button>
      )}
    </>
  );
}

export default App;